import { ArrowRight } from "lucide-react";
import type { AgentSummary, FactoryState } from "../../types";
import { StatusDot, ToneBadge } from "../status-badge";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";

export function AgentCard({
  agent,
  state,
  onOpen,
}: {
  agent: AgentSummary;
  state: FactoryState | undefined;
  onOpen: () => void;
}) {
  const stats = state?.stations[agent.id];
  const active = stats?.active ?? 0;
  const blocked = stats?.blocked ?? 0;
  const providerOk = agent.kind === "policy" || state?.providers[agent.provider]?.configured;
  return (
    <button type="button" onClick={onOpen} className="group text-left">
      <Card className="h-full transition-[transform,box-shadow] duration-200 ease-calm group-hover:-translate-y-0.5 group-hover:shadow-md motion-reduce:transform-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StatusDot tone={blocked > 0 ? "amber" : active > 0 ? "sky" : "neutral"} pulse={active > 0} />
            {agent.name}
            <ArrowRight className="ml-auto size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
          </CardTitle>
          <CardDescription className="line-clamp-2">{agent.description}</CardDescription>
        </CardHeader>
        <CardContent className="mt-auto space-y-3">
          <div className="flex gap-4 text-sm tabular-nums">
            <span>
              <span className="font-semibold">{active}</span>{" "}
              <span className="text-muted-foreground">active</span>
            </span>
            <span>
              <span className="font-semibold">{stats?.queued ?? 0}</span>{" "}
              <span className="text-muted-foreground">queued</span>
            </span>
            {blocked > 0 && (
              <span className="text-amber-600 dark:text-amber-400">
                <span className="font-semibold">{blocked}</span> blocked
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <ToneBadge
              tone={providerOk ? "neutral" : "amber"}
              title={providerOk ? "Provider configured" : "Provider is missing credentials"}
            >
              {agent.kind === "policy" ? `Policy · ${agent.policy}` : agent.provider}
            </ToneBadge>
            {agent.model && <Badge variant="outline">{agent.model}</Badge>}
            {agent.mode === "plan" && <ToneBadge tone="violet">Plan only</ToneBadge>}
            {agent.mcp.required.length + agent.mcp.optional.length > 0 && (
              <Badge variant="outline">{agent.mcp.required.length + agent.mcp.optional.length} tools</Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </button>
  );
}
